import { useCallback, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImagePlus, Upload, Loader2, Check, Trash2 } from "lucide-react";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import { stripImageMetadata } from "@/lib/strip-image-metadata";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useToast } from "@/hooks/use-toast";

/**
 * ReferenceImagesDialog — wspólny popup „Zdjęcia referencyjne".
 *
 * Jedno, spójne źródło prawdy dla zdjęć referencyjnych user-a: tabela
 * brand-assets (firm_creative_assets) przez /api/ad-creator/brand-assets.
 * Co wgrasz tutaj, ląduje też w bibliotece używanej w REKLAMACH i innych
 * kreatorach — i odwrotnie. Obsługuje drag&drop wielu plików naraz.
 */

interface BrandAsset {
  id: string;
  name: string;
  description: string;
  fileUrl: string;
  fileType: string;
}
export interface SelectedRef {
  id: string;
  url: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (selected: SelectedRef[]) => void;
  initialSelectedIds?: string[];
  title?: string;
}

export function ReferenceImagesDialog({
  open,
  onOpenChange,
  onConfirm,
  initialSelectedIds,
  title,
}: Props): JSX.Element {
  const { toast } = useToast();
  const { isAgencyUser, selectedClientId } = useAgencyClients();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelectedIds ?? []));

  const clientParam =
    selectedClientId && selectedClientId !== "none" ? `?clientId=${selectedClientId}` : "";
  const { data, refetch } = useQuery<BrandAsset[]>({
    queryKey: ["brand-assets", selectedClientId],
    queryFn: () => apiRequest("GET", `/api/ad-creator/brand-assets${clientParam}`),
    staleTime: 60_000,
    enabled: open,
  });
  const assets = data ?? [];

  const bulkUpload = useCallback(
    async (files: File[]) => {
      const imgs = files.filter((f) => f.type.startsWith("image/"));
      if (imgs.length === 0) return;
      if (isAgencyUser && (!selectedClientId || selectedClientId === "none")) {
        toast({
          title: "Wybierz klienta",
          description:
            "Najpierw wybierz klienta w selektorze — bez tego zdjęcia nie zostaną przypisane.",
          variant: "destructive",
        });
        return;
      }
      setUploading(true);
      try {
        for (const file of imgs) {
          const fd = new FormData();
          fd.append("file", await stripImageMetadata(file), file.name);
          fd.append("name", file.name.replace(/\.[^.]+$/, "").slice(0, 60) || "reference");
          fd.append("description", "Zdjęcie referencyjne");
          fd.append("fileType", "photo");
          if (selectedClientId && selectedClientId !== "none")
            fd.append("clientId", selectedClientId);
          const resp = await fetch("/api/ad-creator/brand-assets", {
            method: "POST",
            headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
            body: fd,
          });
          if (!resp.ok)
            throw new Error(
              (await resp.json().catch(() => null))?.message || `Upload failed (${resp.status})`
            );
          const created = (await resp.json().catch(() => null)) as { id?: string } | null;
          if (created?.id) setSelected((prev) => new Set(prev).add(created.id!));
        }
        await refetch();
        toast({ title: "Wgrano", description: `${imgs.length} zdj. referencyjnych` });
      } catch (err) {
        toast({
          title: "Nie udało się wgrać",
          description: err instanceof Error ? err.message : "Spróbuj ponownie",
          variant: "destructive",
        });
      } finally {
        setUploading(false);
      }
    },
    [isAgencyUser, selectedClientId, refetch, toast]
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const del = async (id: string) => {
    try {
      await apiRequest("DELETE", `/api/ad-creator/brand-assets/${id}`);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      void refetch();
    } catch {
      /* silent */
    }
  };

  const confirm = () => {
    onConfirm(
      assets
        .filter((a) => selected.has(a.id))
        .map((a) => ({ id: a.id, url: a.fileUrl, name: a.name }))
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/[0.08]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <ImagePlus className="w-5 h-5 text-emerald-400" /> {title ?? "Zdjęcia referencyjne"}
            {selected.size > 0 && (
              <span className="text-xs text-emerald-300/80 font-normal">
                — wybrano {selected.size}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const f = Array.from(e.target.files ?? []);
              if (f.length) void bulkUpload(f);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void bulkUpload(Array.from(e.dataTransfer.files));
            }}
            className={`w-full flex flex-col items-center justify-center gap-1 px-4 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${dragOver ? "border-emerald-500/60 bg-emerald-500/[0.06]" : "border-white/[0.12] hover:border-emerald-500/40 hover:bg-emerald-500/[0.03]"} ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                <span className="text-sm text-white/60">Wgrywanie…</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-white/50" />
                <span className="text-sm text-white/60">
                  Przeciągnij i upuść zdjęcia lub kliknij
                </span>
                <span className="text-[11px] text-white/30">możesz wybrać wiele naraz</span>
              </>
            )}
          </div>
          {assets.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
                  Twoje zdjęcia ({assets.length})
                </span>
                {selected.size > 0 && (
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-[10px] text-white/40 hover:text-white"
                  >
                    Wyczyść wybór
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto">
                {assets.map((a) => {
                  const isSel = selected.has(a.id);
                  return (
                    <div
                      key={a.id}
                      onClick={() => toggle(a.id)}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border transition-all group ${isSel ? "border-emerald-500/70 ring-2 ring-emerald-500/40" : "border-white/[0.08] hover:border-white/[0.2]"}`}
                    >
                      <div className="w-full aspect-square bg-black/40 flex items-center justify-center">
                        <img
                          src={a.fileUrl}
                          alt={a.name}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      {isSel && (
                        <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void del(a.id);
                        }}
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="px-1.5 py-1">
                        <p className="text-[10px] text-white/70 truncate">{a.name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            !uploading && (
              <p className="text-[11px] text-white/40 text-center py-2">
                Brak zdjęć — wgraj pierwsze powyżej
              </p>
            )
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-white/60"
          >
            Zamknij
          </Button>
          <Button
            size="sm"
            onClick={confirm}
            disabled={selected.size === 0}
            className="bg-emerald-500 hover:bg-emerald-400 text-black"
          >
            Użyj wybranych ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
